import { useChat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import { CheckIcon, CircleArrowUpIcon, CopyIcon, SparklesIcon, SquareIcon } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { IconStack } from "@/components/reui/icon-stack";
import { Card } from "@/components/tremor/card";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";

import { adminRoleLabels } from "../../admins/lib/admin-roles";
import { type AssistantPersona, createAdminChatTransport } from "../lib/chat-transport";
import { ChatMarkdown } from "./chat-markdown";
import { isToolPart, ShimmerText, ToolChain, type ToolPartLike } from "./tool-chain";

/**
 * Les suggestions viennent de la fouille du support agence : ce sont les
 * questions réellement posées, pas celles qu'on imagine.
 */
const PORTFOLIO_SUGGESTIONS = [
  "Fais-moi un point sur mes dossiers en médiation",
  "Lesquels de mes dossiers sont bloqués ?",
  "Où en est le dossier #31069 ?",
  "Quels sont les KPIs du portfolio ?",
];

const CLAIM_SUGGESTIONS = [
  "Où en est ce dossier ?",
  "Que répondre au client qui trouve le temps long ?",
  "Quelle est la prochaine étape et sous quel délai ?",
  "La compagnie propose un montant — doivent-ils accepter ?",
];

/**
 * Personas jouables. L'API n'honore la bascule que pour un membre Upclaim — ici
 * on se contente de ne pas afficher le sélecteur aux autres.
 *
 * LAWYER vient d'`adminRoleLabels` : c'est un rôle réel, et deux vocabulaires pour
 * la même chose créent la confusion. UPCLAIM et AGENCY ne sont pas des rôles mais des
 * familles de comptes, d'où leur libellé propre.
 */
const PERSONA_OPTIONS: Array<{ value: AssistantPersona; label: string }> = [
  { value: "UPCLAIM", label: "Upclaim" },
  { value: "LAWYER", label: adminRoleLabels.LAWYER },
  { value: "AGENCY", label: "Agence" },
];

/** Parts groupées pour le rendu : les appels d'outils consécutifs fusionnent en une chaîne. */
type RenderBlock = { kind: "text"; text: string } | { kind: "tools"; parts: ToolPartLike[] };

function groupParts(message: UIMessage): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  for (const part of message.parts) {
    if (part.type === "text") {
      blocks.push({ kind: "text", text: part.text });
      continue;
    }
    if (isToolPart(part)) {
      const last = blocks[blocks.length - 1];
      if (last?.kind === "tools") {
        last.parts.push(part);
      } else {
        blocks.push({ kind: "tools", parts: [part] });
      }
    }
  }
  return blocks;
}

/** « Réflexion… » en shimmer — l'assistant n'a pas encore émis son premier chunk. */
function ThinkingIndicator() {
  return (
    <div className="py-1 text-xs animate-in fade-in duration-300">
      <ShimmerText>Réflexion…</ShimmerText>
    </div>
  );
}


/**
 * Le message client est désormais le chapeau non titré de la réponse : tout ce
 * qui précède la première section (« Ce qu'on sait », « Point de vigilance »).
 *
 * On ne copie que lui — copier la réponse entière transférerait nos sections
 * internes au passager, et sur un compte en marque blanche cela révélerait
 * jusqu'à l'existence d'Upclaim.
 */
function extractClientReply(text: string) {
  const [lead] = text.split(/\n#{2,4}\s/);
  return (lead ?? text).trim();
}

function CopyClientReplyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="ghost"
      size="sm"
      className="mt-1 h-7 text-xs"
      onClick={() => {
        void navigator.clipboard.writeText(extractClientReply(text)).then(() => {
          setCopied(true);
          notify.success("Réponse client copiée");
          setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? <CheckIcon data-icon="inline-start" /> : <CopyIcon data-icon="inline-start" />}
      Copier la réponse client
    </Button>
  );
}

function ChatMessage({ message, streaming }: { message: UIMessage; streaming: boolean }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end animate-in fade-in slide-in-from-bottom-1 duration-200">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-blue-600 px-3.5 py-2 text-sm whitespace-pre-wrap text-white shadow-xs">
          {message.parts.map((part, index) =>
            part.type === "text" ? <span key={`${message.id}-${index}`}>{part.text}</span> : null,
          )}
        </div>
      </div>
    );
  }

  const blocks = groupParts(message);
  const fullText = blocks
    .filter((block): block is { kind: "text"; text: string } => block.kind === "text")
    .map((block) => block.text)
    .join("\n");

  return (
    <div className="max-w-[95%] animate-in fade-in slide-in-from-bottom-1 duration-200">
      {blocks.map((block, index) => {
        if (block.kind === "tools") {
          return <ToolChain key={`${message.id}-tools-${index}`} parts={block.parts} />;
        }
        const isLastBlock = index === blocks.length - 1;
        return (
          <div key={`${message.id}-text-${index}`}>
            <ChatMarkdown>{block.text}</ChatMarkdown>
            {streaming && isLastBlock ? (
              <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse rounded-full bg-gray-400 align-middle" />
            ) : null}
          </div>
        );
      })}
      {!streaming && fullText.trim().length > 0 ? <CopyClientReplyButton text={fullText} /> : null}
    </div>
  );
}

/**
 * Assistant Upclaim, branché sur POST /admin/ai/chat.
 *
 * Un seul chat pour tout : il résout lui-même les dossiers cités (numéro, PNR,
 * nom de passager), en traite plusieurs, et bascule sur le datalake quand la
 * question est agrégée. Le cloisonnement par agence est appliqué côté API, dans
 * chaque outil — ce composant n'a rien à filtrer.
 *
 * `claimId` n'est donc plus une portée mais **le dossier ouvert à l'écran** : un
 * point de départ, que l'utilisateur reste libre de quitter en citant un autre
 * dossier.
 */
export function AdminChat({
  className,
  claimId,
  claimNumber,
}: {
  className?: string;
  /** Présent ⇒ mode dossier : contexte déterministe, aucun outil. */
  claimId?: string;
  claimNumber?: number | null;
}): React.ReactElement {
  const isClaimMode = Boolean(claimId);
  // Seul un membre Upclaim peut rejouer une question sous un autre persona.
  // L'API refuse déjà la bascule aux autres rôles ; on évite juste de leur
  // envoyer un champ qu'elle ignorerait.
  const { isUpclaim: canSwitchPersona } = useCurrentUser();
  const [persona, setPersona] = useState<AssistantPersona>("UPCLAIM");
  const transport = useMemo(
    () => createAdminChatTransport(claimId, canSwitchPersona ? persona : undefined),
    [claimId, canSwitchPersona, persona],
  );
  const { messages, sendMessage, status, stop, setMessages } = useChat({
    transport,
    onError: (error) => {
      notify.error("Le chat IA a rencontré une erreur", { description: error.message });
    },
  });
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const isBusy = status === "submitted" || status === "streaming";
  const lastMessage = messages[messages.length - 1];

  // Chat-internal scroll only (the page itself never auto-scrolls).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isBusy) {
      return;
    }
    void sendMessage({ text: trimmed });
    setInput("");
  };

  return (
    <Card className={cn("flex h-full min-h-[32rem] flex-col p-0", className)}>
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <span className="flex size-6 items-center justify-center rounded-md bg-blue-50">
          <SparklesIcon className="size-3.5 text-blue-500" />
        </span>
        <h3 className="text-sm font-medium text-foreground">Assistant</h3>
        {isClaimMode ? (
          <span className="text-xs text-muted-foreground">dossier #{claimNumber ?? ""}</span>
        ) : null}
        {isBusy ? (
          <span className="flex items-center gap-1.5 text-xs text-blue-600 animate-in fade-in">
            <span className="size-1.5 animate-pulse rounded-full bg-blue-500" />
            en cours
          </span>
        ) : null}
        {canSwitchPersona ? (
          <div className="ml-auto flex items-center gap-2">
            {persona !== "UPCLAIM" ? (
              <span
                className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                title="Aperçu du ton et des outils. Le périmètre reste celui de votre compte : vous voyez toujours tous les dossiers."
              >
                aperçu
              </span>
            ) : null}
            <Select
              value={persona}
              disabled={isBusy}
              onValueChange={(next) => {
                if (typeof next !== "string" || next === persona) {
                  return;
                }
                // Le corpus, les outils et l'expurgation changent avec le
                // persona : rejouer un historique construit sous un autre
                // persona produirait des réponses incohérentes, et le modèle
                // citerait des faits qu'il n'a plus le droit de lire.
                setMessages([]);
                setPersona(next as AssistantPersona);
              }}
            >
              <SelectTrigger className="h-7 w-fit min-w-0 rounded-full border-gray-200 bg-white px-2.5 text-xs" size="sm">
                <SelectValue>{PERSONA_OPTIONS.find((option) => option.value === persona)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {PERSONA_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-left">
        {messages.length === 0 ? (
          <Empty className="h-full justify-center py-8 animate-in fade-in duration-300">
            <EmptyHeader>
              <EmptyMedia>
                <IconStack>
                  <SparklesIcon className="size-5 text-blue-500" />
                </IconStack>
              </EmptyMedia>
              <EmptyTitle>{isClaimMode ? `Assistant du dossier #${claimNumber ?? ""}` : "Assistant"}</EmptyTitle>
              <EmptyDescription>
                {isClaimMode
                  ? "Posez une question sur ce dossier. La réponse s'appuie sur ses données réelles, jamais sur une estimation."
                  : "Posez une question sur un dossier — numéro, référence de réservation ou nom de passager — ou sur l'ensemble de vos dossiers."}
              </EmptyDescription>
            </EmptyHeader>
            <div className="flex flex-wrap justify-center gap-1.5">
              {(isClaimMode ? CLAIM_SUGGESTIONS : PORTFOLIO_SUGGESTIONS).map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => submit(suggestion)}
                  className="cursor-pointer rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-all hover:-translate-y-px hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow-xs"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </Empty>
        ) : (
          messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              streaming={status === "streaming" && message === lastMessage}
            />
          ))
        )}
        {status === "submitted" ? <ThinkingIndicator /> : null}
      </div>

      <form
        className="flex items-center gap-2 border-t border-gray-100 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit(input);
        }}
      >
        <Input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Votre question…"
          className="flex-1"
          disabled={isBusy}
        />
        {isBusy ? (
          <Button type="button" variant="outline" size="icon" aria-label="Arrêter" onClick={() => stop()}>
            <SquareIcon />
          </Button>
        ) : (
          <Button type="submit" size="icon" aria-label="Envoyer" disabled={!input.trim()}>
            <CircleArrowUpIcon />
          </Button>
        )}
      </form>
    </Card>
  );
}
